import React, { useEffect, useState, useCallback } from 'react';
import apiClient from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { ApiKey, ApiKeyCreateForm } from '../../types';
import { Plus, Trash2, Copy, X, Lock, Shield, AlertTriangle, Zap, FlaskConical } from 'lucide-react';
import { IntegrationApiBaseBanner } from '../../components/IntegrationApiBaseBanner';

const ALL_SCOPES = ['shipments:read', 'shipments:write', 'payments:read', 'payments:write'];
const EMPTY_FORM: ApiKeyCreateForm = { name: '', keyType: 'LIVE', scopes: [], allowedIps: '', expiresAt: '' };

const ENV_STYLE: Record<'LIVE' | 'TEST', React.CSSProperties> = {
  LIVE: { background: '#dcfce7', color: '#166534', border: '1px solid #86efac' },
  TEST: { background: '#fef9c3', color: '#854d0e', border: '1px solid #fde047' },
};

export const CompanyAdminApiKeys: React.FC = () => {
  const { user } = useAuth();
  const companyId = user?.companyId ?? '';

  const [companyData, setCompanyData] = useState<any>(null);
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [tab, setTab] = useState<'LIVE' | 'TEST'>('LIVE');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [newKeyType, setNewKeyType] = useState<'LIVE' | 'TEST'>('LIVE');
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<ApiKeyCreateForm>(EMPTY_FORM);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    apiClient.getCompany(companyId).then(res => setCompanyData(res.data.data)).catch(() => {});
  }, [companyId]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await apiClient.listApiKeys(companyId);
      setKeys(res.data.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const apiEnabled = companyData?.allowApiIntegration;
  const filtered = keys.filter(k => (k.keyType || 'LIVE') === tab);

  const toggleScope = (scope: string) =>
    setForm(p => ({
      ...p,
      scopes: p.scopes.includes(scope) ? p.scopes.filter(s => s !== scope) : [...p.scopes, scope],
    }));

  const openForm = (type: 'LIVE' | 'TEST') => {
    setForm({ ...EMPTY_FORM, keyType: type });
    setError('');
    setShowForm(true);
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFormLoading(true);
    try {
      const allowedIps = form.allowedIps.split(',').map(s => s.trim()).filter(Boolean);
      const payload: any = { name: form.name, keyType: form.keyType, scopes: form.scopes };
      if (allowedIps.length > 0) payload.allowedIps = allowedIps;
      if (form.expiresAt) payload.expiresAt = form.expiresAt;
      const res = await apiClient.generateApiKey(companyId, payload);
      setNewKey(res.data.data?.key || null);
      setNewKeyType(form.keyType);
      setTab(form.keyType);
      setShowForm(false);
      setForm(EMPTY_FORM);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate key');
    } finally {
      setFormLoading(false);
    }
  };

  const handleRevoke = async (keyId: string) => {
    if (!window.confirm('Revoke this API key? This cannot be undone.')) return;
    try {
      await apiClient.revokeApiKey(companyId, keyId);
      await load();
    } catch { /* silent */ }
  };

  const handleCopy = () => {
    if (!newKey) return;
    navigator.clipboard.writeText(newKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">API Keys</h1>
        {apiEnabled && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={() => openForm('LIVE')}>
              <Zap size={14} /> New Live Key
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => openForm('TEST')}
              style={{ border: '1px solid #fde047', color: '#854d0e', background: '#fefce8' }}
            >
              <FlaskConical size={14} /> New Test Key
            </button>
          </div>
        )}
      </div>

      <IntegrationApiBaseBanner />

      {companyData && !apiEnabled && (
        <div style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: 10, padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <AlertTriangle size={22} color="#b45309" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 4 }}>API Integration is not enabled</div>
            <div style={{ fontSize: 13, color: '#78350f' }}>Contact your Super Admin to activate API access for your company.</div>
          </div>
        </div>
      )}

      {/* New key reveal */}
      {newKey && (
        <div style={{ ...ENV_STYLE[newKeyType], borderRadius: 10, padding: 18, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 10 }}>
            ⚠️ {newKeyType} key — copy now, it will <u>never</u> be shown again!
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <code style={{ fontSize: 13, background: '#fff', padding: '8px 12px', borderRadius: 6, flex: 1, wordBreak: 'break-all', border: '1px solid #e2e8f0' }}>
              {newKey}
            </code>
            <button className="btn btn-success" onClick={handleCopy} style={{ whiteSpace: 'nowrap' }}>
              <Copy size={14} /> {copied ? 'Copied!' : 'Copy'}
            </button>
            <button className="btn btn-ghost" onClick={() => setNewKey(null)} style={{ padding: '8px' }}>
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Generate form */}
      {showForm && apiEnabled && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
              Generate
              <span style={{ ...ENV_STYLE[form.keyType], borderRadius: 6, padding: '2px 10px', fontSize: 12 }}>
                {form.keyType}
              </span>
              Key
            </h3>
            <button className="btn btn-ghost" onClick={() => setShowForm(false)} style={{ padding: '4px 8px' }}>
              <X size={16} />
            </button>
          </div>

          {error && (
            <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
              {error}
            </div>
          )}

          <form onSubmit={handleGenerate}>
            {/* Environment toggle */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              {(['LIVE', 'TEST'] as const).map(t => (
                <button
                  key={t} type="button"
                  onClick={() => setForm(p => ({ ...p, keyType: t }))}
                  style={{
                    ...ENV_STYLE[t],
                    borderRadius: 6, padding: '6px 18px', fontSize: 13, cursor: 'pointer',
                    fontWeight: form.keyType === t ? 700 : 400,
                    opacity: form.keyType === t ? 1 : 0.5,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  {t === 'LIVE' ? <Zap size={13} /> : <FlaskConical size={13} />} {t}
                </button>
              ))}
            </div>

            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 14, padding: '8px 12px', background: '#f8fafc', borderRadius: 6 }}>
              {form.keyType === 'LIVE'
                ? '🟢 Live keys authenticate real production requests. Keep them secret.'
                : '🟡 Test keys use the same API and database as live keys; only the prefix differs (for rotation and auditing). Use a staging backend if you need isolated data.'}
            </div>

            <div className="form-group" style={{ marginBottom: 16 }}>
              <label>Key Name *</label>
              <input
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                required
                placeholder={form.keyType === 'LIVE' ? 'e.g. Production Integration' : 'e.g. Dev / Staging'}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: '#475569', display: 'block', marginBottom: 8 }}>
                Permissions (Scopes)
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {ALL_SCOPES.map(s => (
                  <label key={s} style={{
                    display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer',
                    padding: '8px 12px',
                    border: `1px solid ${form.scopes.includes(s) ? '#3b82f6' : '#e2e8f0'}`,
                    borderRadius: 6,
                    background: form.scopes.includes(s) ? '#eff6ff' : '#fff',
                  }}>
                    <input type="checkbox" checked={form.scopes.includes(s)} onChange={() => toggleScope(s)} />
                    <code style={{ fontSize: 12 }}>{s}</code>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Shield size={14} /> IP Whitelist
                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>(optional — comma-separated)</span>
              </label>
              <input
                value={form.allowedIps}
                onChange={e => setForm(p => ({ ...p, allowedIps: e.target.value }))}
                placeholder="192.168.1.1, 10.0.0.5"
              />
            </div>

            <div className="form-group" style={{ marginBottom: 20 }}>
              <label>Expiry Date <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
              <input
                type="date"
                value={form.expiresAt}
                onChange={e => setForm(p => ({ ...p, expiresAt: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" className="btn btn-primary" disabled={formLoading}>
                {formLoading ? 'Generating...' : `Generate ${form.keyType} Key`}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* LIVE / TEST tabs */}
      {apiEnabled && (
        <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '2px solid #e2e8f0' }}>
          {(['LIVE', 'TEST'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                padding: '8px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                background: 'none', border: 'none',
                borderBottom: tab === t
                  ? `2px solid ${t === 'LIVE' ? '#16a34a' : '#ca8a04'}`
                  : '2px solid transparent',
                color: tab === t ? (t === 'LIVE' ? '#16a34a' : '#ca8a04') : '#64748b',
                marginBottom: -2,
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {t === 'LIVE' ? <Zap size={13} /> : <FlaskConical size={13} />}
              {t === 'LIVE' ? 'Live Keys' : 'Test Keys'}
              <span style={{ fontSize: 11, background: '#f1f5f9', borderRadius: 10, padding: '1px 7px', color: '#475569' }}>
                {keys.filter(k => (k.keyType || 'LIVE') === t).length}
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="card">
        {loading ? (
          <div className="loading-spinner">Loading keys...</div>
        ) : !apiEnabled ? (
          <div className="empty-state">
            <Lock size={32} color="#cbd5e1" style={{ marginBottom: 12 }} />
            <h3>API Integration Disabled</h3>
            <p style={{ fontSize: 13 }}>Contact your Super Admin to enable API access.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <h3>No {tab.toLowerCase()} keys yet</h3>
            <p>{tab === 'LIVE' ? 'Generate a live key for production integrations.' : 'Generate a test key to safely test your integration.'}</p>
            <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => openForm(tab)}>
              <Plus size={14} /> Generate {tab} Key
            </button>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Prefix</th>
                  <th>Scopes</th>
                  <th>IP Whitelist</th>
                  <th>Status</th>
                  <th>Last Used</th>
                  <th>Expires</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(k => (
                  <tr key={k.id}>
                    <td><strong>{k.name}</strong></td>
                    <td>
                      <span style={{ ...ENV_STYLE[k.keyType || 'LIVE'], borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                        {k.keyType || 'LIVE'}
                      </span>
                    </td>
                    <td><code style={{ fontSize: 12 }}>{k.prefix}...</code></td>
                    <td>
                      {(k.scopes || []).map(s => (
                        <span key={s} style={{ fontSize: 11, background: '#e0f2fe', color: '#075985', borderRadius: 4, padding: '2px 6px', marginRight: 4 }}>
                          {s}
                        </span>
                      ))}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {k.allowedIps?.length
                        ? <span title={k.allowedIps.join(', ')}>{k.allowedIps.length} IP{k.allowedIps.length > 1 ? 's' : ''}</span>
                        : <span style={{ color: '#94a3b8' }}>All IPs</span>}
                    </td>
                    <td><span className={`badge badge-${k.status?.toLowerCase()}`}>{k.status}</span></td>
                    <td style={{ fontSize: 12 }}>
                      {k.lastUsedAt
                        ? <span title={k.lastUsedIp || ''}>{new Date(k.lastUsedAt).toLocaleDateString()}</span>
                        : 'Never'}
                    </td>
                    <td style={{ fontSize: 12 }}>{k.expiresAt ? new Date(k.expiresAt).toLocaleDateString() : 'No expiry'}</td>
                    <td>
                      {k.status === 'ACTIVE' && (
                        <button
                          className="btn btn-danger"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => handleRevoke(k.id)}
                        >
                          <Trash2 size={13} /> Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};
